import {format, parse} from 'date-fns';
import isEqual from 'lodash/isEqual';
import kebabCase from 'lodash/kebabCase';
import snakeCase from 'lodash/snakeCase';

import type {TranslationFunction} from 'src/types/language-support';
import type {BookingSlot, DayOfWeek} from 'src/types/scheduler/settings';
import type {SchedulerTemplateForm} from 'src/types/scheduler/template-form';
import type {Rule} from 'src/types/scheduler/templates';
import type {ScheduledEvent} from 'src/types/scheduler/events';
import type {LocationMeta, SchedulerTemplate} from 'src/types/scheduler';

import {formatIsoDate} from 'src/utils/date-time-2';
import {hasText} from 'src/utils/draft';

import {
  GOOGLE_PROVIDER,
  MICROSOFT_PROVIDER,
  VIRTUAL_CALENDAR_PROVIDER,
} from 'src/components/scheduler/calendar-sync/accounts/utils/constant';
import {getNormalizedServiceAccountProvider} from 'src/components/scheduler/calendar-sync/helper';

import {
  SCHEDULER_MEMBER_MEETINGS_TABLE_CONTEXT,
  SCHEDULER_STAT_CARD_TYPE,
  type SchedulerMeetingsDrilldownRouteType,
} from 'src/components/scheduler/analytics/scheduler-amplitude-constants';
import {TEMPLATE_FORM_PUT_BODY_OMIT_KEYS} from 'src/components/scheduler/template-editor/constants';


const DRILLDOWN_ROUTE_TO_STAT_KEY: Record<
  SchedulerMeetingsDrilldownRouteType,
  keyof typeof SCHEDULER_STAT_CARD_TYPE
> = {
  'meetings-booked': 'MEETINGS_BOOKED',
  'meetings-conducted': 'MEETINGS_CONDUCTED',
  'meetings-cancelled': 'MEETINGS_CANCELLED',
};

const WEEK_ORDER: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const DAY_ABBREV: Record<DayOfWeek, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

export type EventsPageVisitedViewType = 'list' | 'calendar';

export type EventsPageVisitedTimeFilter = 'upcoming' | 'past';

export type EventsPageVisitedAmplitudeProps = {
  view_type: EventsPageVisitedViewType;
  time_filter: EventsPageVisitedTimeFilter;
  active_meeting_type_filters: string;
};

export type EventsEmptyStateViewedAmplitudeProps = {
  time_filter: EventsPageVisitedTimeFilter;
  active_meeting_type_filters: string;
};

export type EventDetailViewedCalendarStatusLabel =
  | 'Accepted'
  | 'Waiting'
  | 'Declined';

export type EventDetailViewedAmplitudeProps = {
  meeting_type: string;
  event_status: EventsPageVisitedTimeFilter;
  has_meeting_link: boolean;
  has_meeting_room: boolean;
  reschedules_remaining: '0' | '1' | '2' | 'No Limit';
  host_status: EventDetailViewedCalendarStatusLabel;
  attendee_status: EventDetailViewedCalendarStatusLabel;
  is_sequential_round: boolean;
};

export type ScheduleNowClickedTimeFilter = 'upcoming' | 'past';

export function getSchedulerCalendarTypeLabel(
  provider: string | null | undefined,
  t: TranslationFunction,
): string {
  const normalized = getNormalizedServiceAccountProvider(provider ?? undefined);
  switch (normalized) {
    case VIRTUAL_CALENDAR_PROVIDER:
      return t('Sense_Calendar', 'Sense Calendar');
    case GOOGLE_PROVIDER:
      return t('Google_Calendar', 'Google Calendar');
    case MICROSOFT_PROVIDER:
      return t('Microsoft_Calendar', 'Microsoft Calendar');
    default:
      return '';
  }
}

export function getSchedulerGoogleOffice365OAuthLabel(
  t: TranslationFunction,
): string {
  return t('GoogleOffice_365', 'Google/Office 365');
}

function parseHHmmToMinutes(hhmm: string): number {
  const [hours, minutes] = hhmm.split(':').map((value) => parseInt(value, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }
  return hours * 60 + minutes;
}

function minutesToHHmm(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}`;
}

function formatTimeTwelveHour(hhmm: string): string {
  const reference = parse(hhmm, 'HH:mm', new Date(2000, 0, 1));
  return format(reference, 'hh:mm a');
}

export function getSchedulerWorkingDaysAbbrevCsv(slots: BookingSlot[]): string {
  const unique = [...new Set(slots.map((slot) => slot.dayOfWeek))];
  unique.sort(
    (day1, day2) => WEEK_ORDER.indexOf(day1) - WEEK_ORDER.indexOf(day2),
  );
  return unique.map((day) => DAY_ABBREV[day]).join(',');
}

export function getSchedulerWorkingHoursStartEndTwelveHour(
  slots: BookingSlot[],
): {
  start_time: string;
  end_time: string;
} {
  if (slots.length === 0) {
    return {start_time: '', end_time: ''};
  }
  let minM = Infinity;
  let maxM = -Infinity;
  for (const slot of slots) {
    const start = parseHHmmToMinutes(slot.startTime);
    const end = parseHHmmToMinutes(slot.endTime);
    minM = Math.min(minM, start);
    maxM = Math.max(maxM, end);
  }
  return {
    start_time: formatTimeTwelveHour(minutesToHHmm(minM)),
    end_time: formatTimeTwelveHour(minutesToHHmm(maxM)),
  };
}

export function buildSettingsWorkingHoursSavedAmplitudeProps(
  timezone: string,
  bookingSlots: BookingSlot[],
): {
  timezone: string;
  working_days: string;
  start_time: string;
  end_time: string;
  time_blocks: number;
} {
  const {start_time, end_time} =
    getSchedulerWorkingHoursStartEndTwelveHour(bookingSlots);
  return {
    timezone: timezone || '',
    working_days: getSchedulerWorkingDaysAbbrevCsv(bookingSlots),
    start_time,
    end_time,
    time_blocks: bookingSlots.length,
  };
}

//Dashboard `user_role`: scheduler admin vs everyone else with dashboard access.
export function getSchedulerDashboardUserRoleLabel(
  isAdmin: boolean,
): 'Admin' | 'Manager' {
  return isAdmin ? 'Admin' : 'Manager';
}

export function getMeetingsDrilldownExportTableContext(
  drilldownType: SchedulerMeetingsDrilldownRouteType,
  isFromTeamMemberDrilldown: boolean,
): string {
  if (isFromTeamMemberDrilldown) {
    return SCHEDULER_MEMBER_MEETINGS_TABLE_CONTEXT;
  }
  return SCHEDULER_STAT_CARD_TYPE[DRILLDOWN_ROUTE_TO_STAT_KEY[drilldownType]];
}

export type TemplateCreatedAmplitudeProps = {
  template_type: string;
  meeting_location_type: string;
  visibility: string;
  booking_flow_type: 'automatic' | 'manual';
  custom_questions_count: number;
  notifications_count: number;
  interviewers_count: number;
  has_conflict_prevention_rules: boolean;
  has_extended_availability: boolean;
  sequence_days_count: number;
};

const SCHEDULER_DISTRIBUTION_RULE_TO_LABEL: Record<string, string> = {
  single_account: '1:1 Individual',
  one_slot_multiple_event: 'Multi-Event',
  panel_interview: 'Panel',
  round_robin_availability: 'Round Robin Availability',
  round_robin_fairness: 'Round Robin Fairness',
  sequential_interview: 'Sequential',
};

export function templatesPageLabel(isTeamTemplate: boolean): string {
  return isTeamTemplate ? 'Team Templates' : 'Templates';
}

const CONFLICT_PREVENTION_RULE_TYPES: ReadonlySet<Rule['ruleType']> = new Set([
  'no_availability_rule',
  'conflict_prevention_date_based',
  'conflict_prevention_event_based',
  'conflict_prevention_event_outside_sense',
]);

const TEMPLATE_FORM_PUT_OMIT_KEY_SET: ReadonlySet<string> = new Set(
  TEMPLATE_FORM_PUT_BODY_OMIT_KEYS,
);

const LOCATION_TYPE_TO_LABEL: Record<
  NonNullable<LocationMeta['type']>,
  string
> = {
  custom_location: 'Custom',
  meeting_link: 'Video',
  meeting_room: 'In-person',
  phone_number: 'Phone Call',
};

/** Dedupe key for empty-state analytics; matches CSV used inside `trackEventsEmptyStateViewed`. */
export function getEventsEmptyStateAnalyticsDedupeKey(
  timeFilter: EventsPageVisitedTimeFilter,
  distributionRulesCsv: string,
): string {
  return `${timeFilter}|${distributionRulesCsv}`;
}

export function getEventsPageActiveMeetingTypeFiltersLabel(
  distributionRulesCsv: string,
): string {
  const ids = distributionRulesCsv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (ids.length === 0) {
    return 'none';
  }
  const labels = ids
    .map((id) => SCHEDULER_DISTRIBUTION_RULE_TO_LABEL[id] ?? id)
    .sort();
  return labels.join(', ');
}

export function buildEventsPageVisitedAmplitudeProps(args: {
  viewType: EventsPageVisitedViewType;
  timeFilter: EventsPageVisitedTimeFilter;
  distributionRulesCsv: string;
}): EventsPageVisitedAmplitudeProps {
  return {
    view_type: args.viewType,
    time_filter: args.timeFilter,
    active_meeting_type_filters: getEventsPageActiveMeetingTypeFiltersLabel(
      args.distributionRulesCsv,
    ),
  };
}

export function buildEventsEmptyStateViewedAmplitudeProps(args: {
  timeFilter: EventsPageVisitedTimeFilter;
  distributionRulesCsv: string;
}): EventsEmptyStateViewedAmplitudeProps {
  return {
    time_filter: args.timeFilter,
    active_meeting_type_filters: getEventsPageActiveMeetingTypeFiltersLabel(
      args.distributionRulesCsv,
    ),
  };
}

export function mapCalendarResponseToAmplitudeStatus(
  status: string | undefined,
): EventDetailViewedCalendarStatusLabel {
  if (status === 'yes') {
    return 'Accepted';
  }
  if (
    status === 'noreply' ||
    status === 'maybe' ||
    status === undefined ||
    status === ''
  ) {
    return 'Waiting';
  }
  return 'Declined';
}

export function getEventDetailMeetingTypeLabel(event: ScheduledEvent): string {
  if (event.groupId) {
    return 'Sequential';
  }
  const rule = event.templateDistributionRule || event.distributionRule;
  return SCHEDULER_DISTRIBUTION_RULE_TO_LABEL[rule] ?? rule;
}

export function formatEventDetailReschedulesRemaining(
  rescheduleAttemptsLeft: number | undefined,
): '0' | '1' | '2' | 'No Limit' {
  if (rescheduleAttemptsLeft == null) {
    return 'No Limit';
  }
  switch (rescheduleAttemptsLeft) {
    case 0:
      return '0';
    case 1:
      return '1';
    case 2:
      return '2';
    default:
      return 'No Limit';
  }
}

export function buildEventDetailViewedAmplitudeProps(
  event: ScheduledEvent,
  eventStatus: EventsPageVisitedTimeFilter,
): EventDetailViewedAmplitudeProps {
  const interviewers = event.interviewers ?? [];
  const host = interviewers.find((i) => i.participantType === 'HOST');
  const candidates = event.candidates ?? [];
  const attendee =
    candidates[0] ?? interviewers.find((i) => i.participantType !== 'HOST');

  return {
    meeting_type: getEventDetailMeetingTypeLabel(event),
    event_status: eventStatus,
    has_meeting_link: Boolean(event.conferencing?.details?.url),
    has_meeting_room: Boolean(event.resources?.[0]),
    reschedules_remaining: formatEventDetailReschedulesRemaining(
      event.rescheduleAttemptsLeft,
    ),
    host_status: mapCalendarResponseToAmplitudeStatus(host?.status),
    attendee_status: mapCalendarResponseToAmplitudeStatus(attendee?.status),
    is_sequential_round: Boolean(event.groupId),
  };
}

export function getEventAmplitudeEventDate(event: ScheduledEvent): string {
  return formatIsoDate(new Date(event.eventStart));
}

export function getReschedulesRemainingIntForPanelInitiated(
  event: ScheduledEvent,
): number {
  const count = event.rescheduleAttemptsLeft;
  if (count == null) {
    return 2;
  }
  return Math.min(2, Math.max(0, count));
}

export function getReschedulesRemainingBeforeForConfirm(
  event: ScheduledEvent,
): number {
  const count = event.rescheduleAttemptsLeft;
  if (count == null) {
    return 2;
  }
  return Math.min(2, Math.max(1, count));
}

export function getSlotDurationMinutes(slot: {
  start_time: string;
  end_time: string;
}): number {
  const [sh, sm] = slot.start_time.split(':').map(Number);
  const [eh, em] = slot.end_time.split(':').map(Number);
  return Math.max(0, eh * 60 + em - (sh * 60 + sm));
}

function getMeetingLocationTypeLabel(
  locationMeta: ReadonlyArray<{type?: LocationMeta['type']}>,
): string {
  const type = locationMeta.find((l) => l.type)?.type;
  return type ? LOCATION_TYPE_TO_LABEL[type] ?? '' : '';
}

function schedulerTemplateHasDescription(template: SchedulerTemplate): boolean {
  const desc = template.description;
  if (!desc) {
    return false;
  }
  if (typeof desc === 'string') {
    return desc.trim().length > 0;
  }
  return hasText(desc as Parameters<typeof hasText>[0]);
}

export function buildDirectMeetingScheduledAmplitudeProps(
  template: SchedulerTemplate,
  args: {
    meetingDate: string;
    startTime: string;
    viewerTimezone: string;
  },
): {
  duration_minutes: number;
  meeting_date: string;
  meeting_time: string;
  timezone: string;
  visibility: 'Public Visibility' | 'Private Visibility';
  meeting_location: string;
  has_meeting_room: boolean;
  has_attachments: boolean;
  has_description: boolean;
  has_interviewers: boolean;
  interviewers_count: number;
} {
  const nonHost = template.booking_accounts.filter((a) => !a.is_host);
  const visibilityValue = template.rules.find(
    (rule) => rule.rule_type === 'visibility_rule',
  )?.value;
  const duration = template.duration;
  const durationMinutes =
    duration.unit === 'minutes'
      ? duration.amount
      : duration.unit === 'hours'
      ? duration.amount * 60
      : 0;
  const parts = args.startTime.split(':');
  const meetingTime = `${(parts[0] ?? '0').padStart(2, '0')}:${(
    parts[1] ?? '00'
  ).padStart(2, '0')}`;
  const hasMeetingRoom = template.location_meta.some(
    (location) => location.type === 'meeting_room',
  );
  const attachmentCount = template.metadata?.attachments?.length ?? 0;

  return {
    duration_minutes: durationMinutes,
    meeting_date: args.meetingDate,
    meeting_time: meetingTime,
    timezone: args.viewerTimezone,
    visibility:
      visibilityValue === 'private'
        ? 'Private Visibility'
        : 'Public Visibility',
    meeting_location: getMeetingLocationTypeLabel(template.location_meta),
    has_meeting_room: hasMeetingRoom,
    has_attachments: attachmentCount > 0,
    has_description: schedulerTemplateHasDescription(template),
    has_interviewers: nonHost.length > 0,
    interviewers_count: nonHost.length,
  };
}

function getVisibilityLabel(rules: Rule[]): string {
  const visibility = rules.find((rule) => rule.ruleType === 'visibility_rule');
  return visibility?.value === 'private' ? 'Private' : 'Public';
}

function countConfiguredNotifications(form: SchedulerTemplateForm): number {
  let count = form.eventReminders?.length ?? 0;
  const confirmationNotifications = form.eventConfirmationNotifications;
  if (!confirmationNotifications) {
    return count;
  }
  count += confirmationNotifications.cancelEvent?.length ?? 0;
  count += confirmationNotifications.scheduleEvent?.length ?? 0;
  count += confirmationNotifications.rescheduleEvent?.length ?? 0;
  count += confirmationNotifications.requestRescheduleEvent?.length ?? 0;
  return count;
}

function hasConflictPreventionRules(rules: Rule[]): boolean {
  return rules.some((rule) =>
    CONFLICT_PREVENTION_RULE_TYPES.has(rule.ruleType),
  );
}

function getSequenceDaysCount(form: SchedulerTemplateForm): number {
  if (form.distributionRule !== 'sequential_interview') {
    return 0;
  }
  const sequence = (
    form.metadata as {sequence?: Array<{type?: string}>} | undefined
  )?.sequence;
  if (!Array.isArray(sequence)) {
    return 0;
  }
  return sequence.filter((sequenceItem) => sequenceItem.type === 'day').length;
}

function hasExtendedAvailability(form: SchedulerTemplateForm): boolean {
  const extendedLimit = form.extendedFutureLimit;
  return extendedLimit != null && extendedLimit.amount > 0;
}

/** Human-readable template type for Amplitude (matches spec display names). */
export function getSchedulerAmplitudeTemplateTypeLabel(
  distributionRule: string,
): string {
  return (
    SCHEDULER_DISTRIBUTION_RULE_TO_LABEL[distributionRule] ?? distributionRule
  );
}

/** Payload properties for `Template Created` (snake_case per spec). */
export function buildTemplateCreatedAmplitudeProps(
  form: SchedulerTemplateForm,
): TemplateCreatedAmplitudeProps {
  return {
    template_type: getSchedulerAmplitudeTemplateTypeLabel(
      form.distributionRule,
    ),
    meeting_location_type: getMeetingLocationTypeLabel(form.locationMeta ?? []),
    visibility: getVisibilityLabel(form.rules ?? []),
    booking_flow_type: form.requireBookingConfirmation ? 'manual' : 'automatic',
    custom_questions_count: form.additionalQuestions?.length ?? 0,
    notifications_count: countConfiguredNotifications(form),
    interviewers_count: form.bookingAccounts?.length ?? 0,
    has_conflict_prevention_rules: hasConflictPreventionRules(form.rules ?? []),
    has_extended_availability: hasExtendedAvailability(form),
    sequence_days_count: getSequenceDaysCount(form),
  };
}

function isUrlSuffixDerivedFromName(form: SchedulerTemplateForm): boolean {
  return form.urlSuffix === kebabCase(form.name ?? '');
}

export function getTemplateUpdatedFieldsChanged(
  before: SchedulerTemplateForm,
  after: SchedulerTemplateForm,
): string {
  const beforeRecord = before as unknown as Record<string, unknown>;
  const afterRecord = after as unknown as Record<string, unknown>;
  const changed: string[] = [];
  for (const key of Object.keys(beforeRecord)) {
    if (TEMPLATE_FORM_PUT_OMIT_KEY_SET.has(key)) {
      continue;
    }
    if (
      key === 'urlSuffix' &&
      isUrlSuffixDerivedFromName(before) &&
      isUrlSuffixDerivedFromName(after)
    ) {
      continue;
    }
    if (!isEqual(beforeRecord[key], afterRecord[key])) {
      changed.push(snakeCase(key));
    }
  }
  return changed.sort().join(',');
}
